import { i18n } from "@lingui/core";

type FormatBytesOptions = {
  decimals?: number;
};

/**
 * Converts a number of bytes into a human-readable string with appropriate units.
 *
 * @param bytes - The number of bytes to format.
 * @param [options={}] - Formatting options.
 * @returns The formatted string representing the size in appropriate units.
 *
 * @example
 * formatBytes(1024); // "1KB"
 * formatBytes(1500, { decimals: 2 }); // "1.46KB"
 */
export function formatBytes(
  bytes: number,
  options: FormatBytesOptions = {},
): string {
  const { decimals = 0 } = options;
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const formatted = Intl.NumberFormat(i18n.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(bytes / k ** i);

  return `${formatted}${sizes[i]}`;
}

/**
 * Formats a given width and height into a human-readable aspect ratio string.
 *
 * @param params - The input dimensions.
 * @param params.width - The width value.
 * @param params.height - The height value.
 * @returns A formatted aspect ratio string (e.g., "16:9").
 *
 * @example
 * formatAspectRatio({ width: 1920, height: 1080 }); // "16:9"
 */
export function formatAspectRatio({
  width,
  height,
}: { width: number; height: number }): string {
  const formattedWidth = Intl.NumberFormat(i18n.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(width);
  const formattedHeight = Intl.NumberFormat(i18n.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(height);

  return `${formattedWidth}:${formattedHeight}`;
}
